import React, { useState, useEffect } from 'react';

// Store
import useLocalWishlistStore from '@src/stores/localWishlistStore';

// Hooks
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';

// Utils
import shopifyHelpers from 'frontend/js/shopify';

// Comps
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import { Link } from '@arctheme-components/elements/Link/Link';
import ProductTile from '@src/stories/ecommerce/Product/ProductTile';
import { LoadingSpinner } from '@arctheme-components/elements/LoadingSpinner/LoadingSpinner';
import { ListProducts } from '@src/stories/sections/ListProducts/ListProducts';
import { Headings } from '@arctheme-components/elements/Headings/Headings';

// Stylings
import './WishlistProducts.scss'

export const WishlistProducts = ({ settings }) => {
  const isLoggedIn = (window as any).customer.isLoggedIn;

  // Settings
  const {
    mobile_breakpoint,
    section: { layout_type, layout_type_mobile, title_registration, content_registration },
    locales
  } = settings;

  // Hooks
  const isMobile = useMobileBreakpoint(mobile_breakpoint);

  // Store
  // Local storage wishlist
  const localLists = useLocalWishlistStore((state) => state.localLists);

  // States
  const [products, setProducts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Re-fetch products when there are updates in wishlist
  useEffect(() => {
    const fetchProducts = async () => {
      const wishlist = localLists?.[0];

      if (!wishlist) {
        setProducts(null);
        setIsLoading(false);
        return;
      }

      // Filter products that have been fetched on the first load
      // rather than re-fetching when removed. Otherwise, refetch
      if (products && products.length && products.length > wishlist.products.length) {
        setProducts((prev) =>
          prev.filter((product) => wishlist.products.find((productId) => productId.includes(product.id)))
        );

        return;
      }

      const graphQlProducts = await shopifyHelpers.getShopifyProductList(wishlist.products);

      setProducts(graphQlProducts);
      setIsLoading(false);
    };

    fetchProducts();
  }, [localLists]);

  return (
    <div className='wishlist-products'>
      {!isLoggedIn &&
      ((isMobile && layout_type_mobile === 'list-registration-cta') ||
        (!isMobile && layout_type === 'list-registration-cta')) ? (
        <div className='md:flex justify-center items-start md:bg-white'>
          <div className='sticky top-[var(--header-height)] md:top-[calc(var(--header-height)+1rem)] z-[1]'>
            <div className='max-lg:mb-6 md:min-w-[250px] lg:min-w-[328px] md:pt-8'>
              <div className='md:px-[31px]'>
                <div className='md:max-w-[215px]'>
                  {title_registration && (
                    <Headings
                      type='h4'
                      text={title_registration}
                      classes='h4 mb-6'
                    />
                  )}

                  {content_registration && (
                    <p
                      dangerouslySetInnerHTML={{ __html: content_registration }}
                      className='b2 [&_a]:underline'
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className={`md:px-8 xl:px-16 md:pt-8 hide-scrollbar max-md:bg-transparent ${
              products && products.length ? 'md:min-h-[400px]' : 'h-[50vh] lg:h-[80vh]'
            }`}
          >
            {isLoading ? (
              // TODO: Replace with appropriate loader
              <div className='pb-4'>
                <LoadingSpinner id='spinner-account-wishlist' />
              </div>
            ) : products && products.length ? (
              <div className='grid grid-cols-2 gap-x-4 xl:gap-x-[38px] gap-y-4 lg:gap-y-[31px] pb-6'>
                {products.map((product) => (
                  <ProductTile
                    key={product.id}
                    product={product}
                    swatches={product?.swatches}
                    siblings={undefined}
                    settings={settings}
                    enableWishlist={true}
                    type='wishlist'
                  />
                ))}
              </div>
            ) : (
              <div className='text-center pb-4'>
                <Paragraph
                  type='b2 text-center'
                  text={locales.empty}
                />
                <Link
                  link='/collections/all'
                  classes='button btn-secondary mt-6'
                >
                  Continue Shopping
                </Link>
              </div>
            )}
          </div>
        </div>
      ) : (
        products &&
        products.length > 0 && (
          <ListProducts
            settings={settings}
            products={products}
          />
        )
      )}
    </div>
  );
};
